import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { Box, Grid } from "@mui/material";
import { TitleAndActionBox } from "../../../common";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../../../context/AuthContext";
import {
  HeadingText,
  ParagraphText,
  UBButton,
  UBInput,
} from "../../../../Libraries";
import formStyles from "../../SaleExpense/SaleExpenseForm/styles.module.css";
import { messageTemplate, sentenceCase } from "../../../../constants";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { formatPhoneForPKCode } from "../../../../utils";
import {
  CopyIconNew,
  CustomerEditIcon,
  RupinNewLogo,
  Whatsapp,
} from "../../../../assets/icons/icon";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { useWalletContext } from "../../../../context/WalletContext";
import rmStyles from "./RequestMoney.module.css";

const CollectPaymentView = () => {
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { business } = useAuthContext();
  const { walletData } = useWalletContext();
  const [copyKey, setCopyKey] = useState("");
  const raastDetail = walletData?.account_info?.raast_info?.raast;

  const customerState = location?.state || {};
  const customerName = customerState.name || "";
  const customerPhone = customerState.phone || "";
  const defaultAmount = customerState.total_outstanding_payment || "";

  const [amount, setAmount] = useState(() => {
    if (!defaultAmount) return "";
    const num = parseFloat(defaultAmount);
    if (isNaN(num)) return "";
    return String(Math.floor(num * 100) / 100);
  });
  const [prevAmount, setPrevAmount] = useState("");
  const [isEditingAmount, setIsEditingAmount] = useState(false);

  const startEditing = () => {
    triggerLogEvent(analytics, "w_req_edit_amount");
    setPrevAmount(amount);
    setIsEditingAmount(true);
  };

  const cancelEditing = () => {
    setAmount(prevAmount);
    setIsEditingAmount(false);
  };
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!customerName) {
      navigate("/UCash/PendingPaymentCollection", { replace: true });
    }
  }, [customerName, navigate]);

  const accountDetails = useMemo(
    () => ({
      bankName: raastDetail?.bank_name || "",
      accountNumber: raastDetail?.raast_details?.account_number || "",
      accountTitle: raastDetail?.raast_details?.name || "",
      IBAN: raastDetail?.raast_details?.iban || "",
      qr_share_key: raastDetail?.raast_details?.qr_share_key,
    }),
    [raastDetail],
  );

  const copyDetailsText = useMemo(
    () =>
      `Account title: ${accountDetails.accountTitle}
Account number: ${accountDetails.accountNumber}
IBAN: ${accountDetails.IBAN}`,
    [accountDetails],
  );

  const handleCopy = async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyKey(key);
      triggerLogEvent(analytics, "w_req_copy_detail", { type: key });
      setTimeout(() => setCopyKey(""), 1500);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCopyAllDetails = () => {
    triggerLogEvent(analytics, "w_req_copy_details");
    handleCopy(copyDetailsText, "all");
  };

  const handleCollectPayment = async () => {
    if (!amount) return;
    setIsLoading(true);
    try {
      const phone = formatPhoneForPKCode(customerPhone);
      const encodedMessage = encodeURIComponent(
        messageTemplate(
          formatNum(amount),
          business?._raw?.name,
          accountDetails,
        ),
      );
      triggerLogEvent(analytics, "w_req_sent");
      const whatsappLink = `https://web.whatsapp.com/send?phone=${phone}&text=${encodedMessage}`;
      window.open(whatsappLink, "_blank");
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = !amount || isLoading;

  return (
    <Fragment>
      <Grid
        container
        className={`${formStyles.root} ${formStyles.SaleExpenseFormWrapper} ${rmStyles.collectMoneyWrapper}`}
      >
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: I18n.collect || "Collect",
              isBack: true,
              onBack: () => navigate(-1),
              id: "collect_payment",
            }}
          />
          <Box className={rmStyles.collectContent}>
            <HeadingText tag="h1" className={rmStyles.collectHeading}>
              {I18n.collect_from || "Collect from"} {sentenceCase(customerName)}
            </HeadingText>

            <Box className={rmStyles.balanceDueCard}>
              <Box className={rmStyles.balanceDueHeader}>
                <ParagraphText className={rmStyles.balanceDueLabel}>
                  {I18n.balance_due || "Balance due"}
                </ParagraphText>
              </Box>
              {isEditingAmount ? (
                <Box className={rmStyles.amountEditWrapper}>
                  <UBInput
                    inputId="collect_amount_input"
                    onlyNumbers
                    autoFocus
                    value={amount}
                    onChange={(_, value) => setAmount(value)}
                    maxLength={8}
                    placeholder={I18n.enter_amount_here}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setIsEditingAmount(false);
                      if (e.key === "Escape") cancelEditing();
                    }}
                  />
                  <UBButton
                    id="request_money_amount_apply_btn"
                    title={I18n.apply}
                    onClick={() => setIsEditingAmount(false)}
                    className={`${rmStyles.amountApplyBtn} ubButtonStyle ubPrimaryButton textUppercase`}
                  />
                  <UBButton
                    id="request_money_cancel_edit_btn"
                    title={I18n.cancel || "Cancel"}
                    onClick={cancelEditing}
                    className={`${rmStyles.amountCancelBtn} ubButtonStyle ubSecondaryButton textUppercase`}
                  />
                </Box>
              ) : (
                <Box className={rmStyles.balanceDueAmountWrapper}>
                  <HeadingText tag="h2" className={rmStyles.balanceDueAmount}>
                    Rs. {formatNum(amount || 0)}
                  </HeadingText>
                  <button
                    id="request_money_edit_amount_btn"
                    type="button"
                    className={rmStyles.editAmountBtn}
                    onClick={startEditing}
                    aria-label={I18n.edit}
                  >
                    <CustomerEditIcon width={20} height={20} />
                  </button>
                </Box>
              )}
            </Box>

            <Box className={rmStyles.accountCard}>
              <Box className={rmStyles.accountCardWatermark}></Box>
              <Box className={rmStyles.accountCardHeader}>
                <RupinNewLogo />
              </Box>

              <Box className={rmStyles.accountField}>
                <ParagraphText className={rmStyles.accountFieldLabel}>
                  {I18n.account_title}
                </ParagraphText>
                <ParagraphText className={rmStyles.accountFieldValue}>
                  {accountDetails.accountTitle}
                </ParagraphText>
              </Box>

              <Box className={rmStyles.accountField}>
                <ParagraphText className={rmStyles.accountFieldLabel}>
                  {I18n.account_number}
                </ParagraphText>
                <Box className={rmStyles.accountFieldValueWrapper}>
                  <ParagraphText className={rmStyles.accountFieldValue}>
                    {accountDetails.accountNumber}
                  </ParagraphText>
                  <button
                    id="request_money_copy_account_number_btn"
                    type="button"
                    className={rmStyles.copyIconBtn}
                    onClick={() =>
                      handleCopy(accountDetails.accountNumber, "accountNumber")
                    }
                    aria-label={I18n.copy}
                  >
                    <CopyIconNew width={18} height={18} />
                    {copyKey === "accountNumber" ? I18n.copied : ""}
                  </button>
                </Box>
              </Box>

              <Box className={rmStyles.accountField}>
                <ParagraphText className={rmStyles.accountFieldLabel}>
                  {I18n.IBAN}
                </ParagraphText>
                <Box className={rmStyles.accountFieldValueWrapper}>
                  <ParagraphText className={rmStyles.accountFieldValue}>
                    {accountDetails.IBAN}
                  </ParagraphText>
                  <button
                    id="request_money_copy_iban_btn"
                    type="button"
                    className={rmStyles.copyIconBtn}
                    onClick={() => handleCopy(accountDetails.IBAN, "iban")}
                    aria-label={I18n.copy}
                  >
                    <CopyIconNew width={18} height={18} />
                    {copyKey === "iban" ? I18n.copied : ""}
                  </button>
                </Box>
              </Box>
            </Box>

            <Box className={rmStyles.actionRow}>
              <UBButton
                id="request_money_copy_details_btn"
                title={
                  copyKey === "all"
                    ? I18n.copied
                    : I18n.copy_details || "Copy details"
                }
                onClick={handleCopyAllDetails}
                iconComponent={<CopyIconNew width={20} height={20} />}
                className={`${rmStyles.copyDetailsBtn} ubButtonStyle ubSecondaryButton textUppercase`}
              />
              <UBButton
                id="request_money_collect_payment_btn"
                title={I18n.collect_payment}
                onClick={handleCollectPayment}
                loading={isLoading}
                disabled={isDisabled}
                iconComponent={<Whatsapp width={20} height={20} />}
                className={`${rmStyles.collectPaymentBtn} ubButtonStyle ubPrimaryButton textUppercase`}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default CollectPaymentView;
